import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, BookOpen, Building2, Award, TrendingUp, DollarSign, Download, Loader2,
  BarChart3, PieChart, ArrowUp, ArrowDown,
} from 'lucide-react';

const SystemReportsPage = () => {
  const { api } = useAuth();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [growthPeriod, setGrowthPeriod] = useState('monthly');
  const [popularity, setPopularity] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
    fetchGrowth();
    fetchPopularity();
    fetchRevenue();
    fetchOrgs();
  }, []);

  useEffect(() => { fetchGrowth(); }, [growthPeriod]);

  const fetchOverview = async () => {
    try { const r = await api.get('/admin/reports/overview'); setOverview(r.data); } catch {} finally { setLoading(false); }
  };
  const fetchGrowth = async () => {
    try { const r = await api.get(`/admin/reports/user-growth?period=${growthPeriod}`); setGrowth(r.data); } catch {}
  };
  const fetchPopularity = async () => {
    try { const r = await api.get('/admin/reports/course-popularity'); setPopularity(r.data || []); } catch {}
  };
  const fetchRevenue = async () => {
    try { const r = await api.get('/admin/reports/revenue'); setRevenue(r.data); } catch {}
  };
  const fetchOrgs = async () => {
    try { const r = await api.get('/admin/reports/organisations'); setOrgs(r.data || []); } catch {}
  };

  const exportCSV = async (type) => {
    try {
      const res = await api.get(`/admin/reports/export/csv?report_type=${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <DashboardLayout requiredRoles={['super_admin']}><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div></DashboardLayout>;

  const maxGrowth = growth?.data ? Math.max(...growth.data.map(d => d.users), 1) : 1;

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="system-reports-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">System Reports</h1>
            <p className="text-slate-500 mt-1 text-sm">Platform analytics and insights</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Overview</TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Revenue</TabsTrigger>
            <TabsTrigger value="growth" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Growth</TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Courses</TabsTrigger>
            <TabsTrigger value="orgs" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Building2 className="h-3.5 w-3.5" /> Organisations</TabsTrigger>
          </TabsList>

          {/* ─── OVERVIEW TAB ─── */}
          <TabsContent value="overview">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportCSV('overview')} data-testid="export-overview-btn"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            </div>
            {overview && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Users', value: overview.total_users, icon: Users, color: 'text-[#20acf6]', sub: `${overview.new_users_30d} new (30d)` },
                  { label: 'Active Users', value: overview.active_users, icon: Users, color: 'text-green-600' },
                  { label: 'Organisations', value: overview.total_organisations, icon: Building2, color: 'text-purple-600' },
                  { label: 'Total Courses', value: overview.total_courses, icon: BookOpen, color: 'text-amber-600', sub: `${overview.published_courses} published` },
                  { label: 'Enrollments', value: overview.total_enrollments, icon: TrendingUp, color: 'text-indigo-600', sub: `${overview.new_enrollments_30d} new (30d)` },
                  { label: 'Completions', value: overview.total_completions, icon: Award, color: 'text-teal-600' },
                  { label: 'Certificates', value: overview.total_certificates, icon: Award, color: 'text-rose-600' },
                  { label: 'Draft Courses', value: overview.draft_courses, icon: BookOpen, color: 'text-slate-500' },
                ].map((s, i) => (
                  <Card key={i} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <s.icon className={`h-5 w-5 ${s.color}`} />
                      </div>
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                      {s.sub && <p className="text-[10px] text-slate-400 mt-1">{s.sub}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ─── REVENUE TAB ─── */}
          <TabsContent value="revenue">
            {revenue && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Card className="border-slate-200"><CardContent className="p-5 text-center">
                    <p className="text-3xl font-bold text-green-600">£{revenue.mrr.toLocaleString()}</p>
                    <p className="text-sm text-slate-500 mt-1">Monthly Recurring Revenue</p>
                  </CardContent></Card>
                  <Card className="border-slate-200"><CardContent className="p-5 text-center">
                    <p className="text-3xl font-bold text-blue-600">£{revenue.arr.toLocaleString()}</p>
                    <p className="text-sm text-slate-500 mt-1">Annual Recurring Revenue</p>
                  </CardContent></Card>
                  <Card className="border-slate-200"><CardContent className="p-5 text-center">
                    <p className="text-3xl font-bold text-purple-600">{revenue.total_paying_orgs}</p>
                    <p className="text-sm text-slate-500 mt-1">Paying Organisations</p>
                  </CardContent></Card>
                </div>
                <Card className="border-slate-200">
                  <CardHeader><CardTitle className="text-lg">Subscription Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    {revenue.tier_breakdown?.length > 0 ? (
                      <div className="space-y-3">
                        {revenue.tier_breakdown.map((tb, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-800">{tb.tier}</p>
                              <p className="text-xs text-slate-500">{tb.count} organisation{tb.count !== 1 ? 's' : ''}</p>
                            </div>
                            <p className="font-bold text-green-600">£{tb.mrr}/mo</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-8">No subscription data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* ─── GROWTH TAB ─── */}
          <TabsContent value="growth">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">User Growth</CardTitle>
                <div className="flex gap-2">
                  <Select value={growthPeriod} onValueChange={setGrowthPeriod}>
                    <SelectTrigger className="w-32" data-testid="growth-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => exportCSV('users')}><Download className="h-4 w-4 mr-2" /> Export</Button>
                </div>
              </CardHeader>
              <CardContent>
                {growth?.data ? (
                  <div className="space-y-2">
                    {growth.data.map((d, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-16 shrink-0 text-right">{d.label}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden">
                          <div className="bg-[#20acf6] h-full rounded-full transition-all" style={{ width: `${Math.max((d.users / maxGrowth) * 100, d.users > 0 ? 3 : 0)}%` }} />
                        </div>
                        <span className="text-xs font-medium text-slate-700 w-8">{d.users}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No growth data</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── COURSES TAB ─── */}
          <TabsContent value="courses">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportCSV('courses')}><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            </div>
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Course Popularity Ranking</CardTitle></CardHeader>
              <CardContent className="p-0">
                {popularity.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">#</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Course</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Enrollments</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Completions</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {popularity.map((c, i) => (
                          <tr key={c.course_id} className="border-b border-slate-100">
                            <td className="px-4 py-3 font-bold text-slate-400">{i + 1}</td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-slate-800">{c.title}</p>
                              <p className="text-xs text-slate-500">{c.category}</p>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={c.status === 'published' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>{c.status}</Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{c.enrollments}</td>
                            <td className="px-4 py-3 text-slate-600">{c.completions}</td>
                            <td className="px-4 py-3 text-slate-600">{c.completion_rate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">No course data</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── ORGANISATIONS TAB ─── */}
          <TabsContent value="orgs">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportCSV('organisations')}><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            </div>
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Organisations</CardTitle></CardHeader>
              <CardContent className="p-0">
                {orgs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Tier</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Members</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Free Access</th>
                          <th className="px-4 py-3 text-left font-medium text-slate-600">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgs.map(o => (
                          <tr key={o.id} className="border-b border-slate-100">
                            <td className="px-4 py-3 font-medium text-slate-800">{o.name}</td>
                            <td className="px-4 py-3">
                              <Badge className={o.status === 'approved' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>{o.status}</Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{o.tier_name}</td>
                            <td className="px-4 py-3 text-slate-600">{o.member_count}/{o.max_members}</td>
                            <td className="px-4 py-3">{o.is_free_access ? <Badge className="bg-teal-100 text-teal-700 text-[10px]">Yes</Badge> : '—'}</td>
                            <td className="px-4 py-3 text-slate-500">{o.created_at?.slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">No organisations</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SystemReportsPage;
